import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Form, Table } from "react-bootstrap";
import {
  Users,
  RefreshCw,
  Plus,
  Trash2,
  Power,
  AlertTriangle,
} from "lucide-react";
import { AppButton, ConfirmModal, StatusBadge, EmptyState, SearchInput } from "../../ui";
import apiClient from "../../../utils/apiClient";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const statusToBadge = (s) => {
  if (s === "available") return "active";
  if (s === "busy") return "ongoing";
  if (s === "disabled") return "destructive";
  return "inactive";
};

const OrganizerPoolPanel = ({ showToast, platformConnected }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pool, setPool] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null); // host obj

  const refresh = useCallback(async () => {
    const { data } = await apiClient.get("/sa/zoho-meeting/pool");
    setPool(Array.isArray(data) ? data : data?.hosts || []);
  }, []);

  useEffect(() => {
    (async () => {
      try { await refresh(); } catch { /* silent */ }
      setLoading(false);
    })();
  }, [refresh]);

  const handleSync = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/sa/zoho-meeting/pool/sync-from-zoho");
      showToast?.(`Sync complete: +${data.added} added, ${data.updated} updated.`, "success");
      await refresh();
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Sync failed.", "error");
    }
    setBusy(false);
  };

  const handleAdd = async () => {
    if (!addEmail.trim()) return;
    setBusy(true);
    try {
      await apiClient.post("/sa/zoho-meeting/pool", {
        email: addEmail.trim(),
        displayName: addName.trim(),
      });
      setAddOpen(false);
      setAddEmail("");
      setAddName("");
      showToast?.("Organizer added.", "success");
      await refresh();
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Add failed.", "error");
    }
    setBusy(false);
  };

  const handleToggleStatus = async (host) => {
    const next = host.status === "disabled" ? "available" : "disabled";
    setBusy(true);
    try {
      await apiClient.patch(`/sa/zoho-meeting/pool/${host._id}`, { status: next });
      showToast?.(`Organizer ${next === "available" ? "enabled" : "disabled"}.`, "success");
      await refresh();
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Update failed.", "error");
    }
    setBusy(false);
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setBusy(true);
    try {
      await apiClient.delete(`/sa/zoho-meeting/pool/${confirmRemove._id}`);
      setConfirmRemove(null);
      showToast?.("Organizer removed.", "success");
      await refresh();
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Remove failed.", "error");
    }
    setBusy(false);
  };

  const filtered = pool.filter((h) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(h.email || "").toLowerCase().includes(q) ||
      String(h.displayName || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="zoho-pool">
      <div className="zoho-pool__toolbar">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search organizers…"
        />
        <div className="zoho-pool__toolbar-actions">
          <AppButton
            variant="secondary"
            size="sm"
            onClick={handleSync}
            disabled={busy || !platformConnected}
            title={!platformConnected ? "Connect the platform Zoho account first" : "Pull users from Zoho"}
          >
            <RefreshCw size={14} className="me-1" /> Sync from Zoho
          </AppButton>
          <AppButton variant="primary" size="sm" onClick={() => setAddOpen(true)} disabled={busy}>
            <Plus size={14} className="me-1" /> Add Organizer
          </AppButton>
        </div>
      </div>

      {!platformConnected && (
        <div className="zoho-pool__notice">
          <AlertTriangle size={14} className="me-1" />
          Platform Zoho account not connected. The pool can still be edited, but classes will not start
          until the account is connected.
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center p-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title={pool.length === 0 ? "No organizers in pool" : "No matches"}
          description={
            pool.length === 0
              ? "Add organizer users from your Zoho Meeting account, or click Sync from Zoho to pull them automatically."
              : "Try a different search term."
          }
        />
      ) : (
        <div className="zoho-pool__table-wrap">
          <Table className="zoho-pool__table" hover responsive>
            <thead>
              <tr>
                <th>Organizer</th>
                <th>Status</th>
                <th>Current Use</th>
                <th>Meetings</th>
                <th>Failures</th>
                <th>Last Released</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr key={h._id}>
                  <td>
                    <div className="zoho-pool__email">{h.email}</div>
                    {h.displayName && <div className="zoho-pool__name">{h.displayName}</div>}
                    {h.zuid && <div className="zoho-pool__zuid">zuid: {h.zuid}</div>}
                  </td>
                  <td><StatusBadge status={statusToBadge(h.status)}>{h.status}</StatusBadge></td>
                  <td>
                    {h.status === "busy" ? (
                      <span className="zoho-pool__busy">
                        Batch <code>{String(h.currentBatchId || "").slice(-6)}</code>
                      </span>
                    ) : (
                      <span className="zoho-pool__idle">Idle</span>
                    )}
                  </td>
                  <td>{h.meetingCount || 0}</td>
                  <td>
                    {h.consecutiveFailures > 0 ? (
                      <span className="zoho-pool__fail">{h.consecutiveFailures}</span>
                    ) : (
                      <span className="zoho-pool__ok">0</span>
                    )}
                  </td>
                  <td>{fmtDate(h.lastReleasedAt)}</td>
                  <td className="text-end">
                    <AppButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(h)}
                      disabled={busy || h.status === "busy"}
                      title={h.status === "disabled" ? "Enable" : "Disable"}
                    >
                      <Power size={13} className="me-1" />
                      {h.status === "disabled" ? "Enable" : "Disable"}
                    </AppButton>
                    <AppButton
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmRemove(h)}
                      disabled={busy || h.status === "busy"}
                    >
                      <Trash2 size={13} />
                    </AppButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add organizer modal */}
      <ConfirmModal
        show={addOpen}
        title="Add Zoho organizer"
        message={
          <div className="zoho-pool__add-form">
            <Form.Group className="mb-2">
              <Form.Label className="sa-settings__label">Email</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="organizer@yourdomain.com"
                autoFocus
              />
              <Form.Text muted style={{ fontSize: "0.7rem" }}>
                Must be a user that exists in your Zoho Meeting organization. Run Sync from Zoho afterwards
                to populate the zuid.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label className="sa-settings__label">Display name (optional)</Form.Label>
              <Form.Control
                size="sm"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Class Host #1"
              />
            </Form.Group>
          </div>
        }
        confirmText="Add"
        variant="info"
        onConfirm={handleAdd}
        onHide={() => setAddOpen(false)}
        confirmDisabled={!addEmail.trim() || busy}
      />

      {/* Remove confirm */}
      <ConfirmModal
        show={!!confirmRemove}
        title="Remove organizer?"
        message={
          confirmRemove
            ? `Remove ${confirmRemove.email} from the pool? The user is not deleted from Zoho — they simply won't be allocated to new classes.`
            : ""
        }
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemove}
        onHide={() => setConfirmRemove(null)}
      />
    </div>
  );
};

export default OrganizerPoolPanel;
